// Retry wrapper for the reporting admission gate. The heavy reporting
// endpoints answer HTTP 429 (Xano `toomanyrequests`) when the instance is
// already running its cap of cold computations (functions/reporting_admission
// v2, 2026-09-11: lease-counted concurrency; a rejection consumes no budget and
// a duplicate of an in-flight key is rejected until that compute lands in the
// cache). The right client response is to wait briefly and ask again, not to
// show an error: an early retry is cheap now, so the schedule starts at 1 s,
// and each wait carries ±30% jitter so paired callers (operations + visit
// alerts) stop retrying in lockstep. Only 429 is retried; AbortError and every
// other failure propagate immediately. An abort during a wait rejects with
// AbortError so superseded requests stay silent.
const DEFAULT_DELAYS = [1000, 2000, 3000, 5000, 8000, 8000, 8000];
const JITTER = 0.3;

function jittered(ms) {
  const spread = ms * JITTER;
  return Math.max(250, Math.round(ms - spread + Math.random() * spread * 2));
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Run `run()` and retry it on HTTP 429 with the given backoff schedule.
 * @param {() => Promise<any>} run
 * @param {{ signal?: AbortSignal, delays?: number[] }} [options]
 */
export async function retryOnBusy(run, { signal, delays = DEFAULT_DELAYS } = {}) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await run();
    } catch (err) {
      const busy = err?.status === 429 && err?.name !== 'AbortError';
      if (!busy || attempt >= delays.length) throw err;
      await sleep(jittered(delays[attempt]), signal);
    }
  }
}
